import { useRef, useState } from "react";
import { useMutation } from "convex/react";
import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { AlertCircle, FileSpreadsheet, Loader2, Plus, Save, Undo2, X } from "lucide-react";
import { toast } from "sonner";

import {
  EditableDataGrid,
  type EditableDataGridHandle,
} from "@/components/editable-data-grid";
import {
  SpecPilotSourcePdfPanel,
  type SourcePdfRef,
} from "@/components/spec-pilot-source-pdf-panel";
import { uploadTableRowsPayload } from "@/lib/table-rows-upload";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";

// ============================================================================
// Types
// ============================================================================

export interface TableEditorModalProps {
  table: {
    _id: Id<"specPilotExtractedTables">;
    name?: string;
    headers: string[];
    rows: string[][];
    pageNumber: number;
    confidence?: number;
    extractionMethod?: string;
    selectedForSubmittal?: boolean;
  };
  isOpen: boolean;
  onClose: () => void;
  sourcePdf?: SourcePdfRef | null;
  sourceId?: string;
}

// ============================================================================
// Component
// ============================================================================

/**
 * Full-screen editor modal for a hydrated SpecPilot extracted table.
 *
 * @param props - {@link TableEditorModalProps}
 * @param props.table - Hydrated extracted table data, including the Convex table
 *   ID, editable `headers`/`rows`, source `pageNumber`, and optional display
 *   metadata (`name`, `confidence`, `extractionMethod`, `selectedForSubmittal`).
 * @param props.isOpen - Controls whether the dialog is visible. When false, the
 *   modal is closed and its content is hidden by the dialog component.
 * @param props.onClose - Called when the user dismisses the modal or when the
 *   dialog open state changes to false.
 * @param props.sourcePdf - Optional signed source PDF reference for the side-by-side
 *   viewer. Pass `null` when unavailable; `fileUrl` is expected to be a browser
 *   fetchable URL and `filename` is used for viewer labeling/media detection.
 * @param props.sourceId - Optional stable source identifier used as the document
 *   viewport revision key. When omitted, the PDF panel is not rendered.
 */
export function TableEditorModal({
  table,
  isOpen,
  onClose,
  sourcePdf,
  sourceId,
}: TableEditorModalProps) {
  const [hasError, setHasError] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [canUndoRowAction, setCanUndoRowAction] = useState(false);
  const gridRef = useRef<EditableDataGridHandle>(null);
  
  // Mutation for updating table data
  const updateTableData = useMutation(api.specPilot.extractedTables.updateData);
  const generateR2UploadUrl = useMutation(api.files.generateR2UploadUrl);

  // Handle save
  const handleSave = async (
    headers: string[],
    rows: string[][],
    metadata?: { deletedRowIndices: number[] }
  ) => {
    try {
      const uploadedRows = await uploadTableRowsPayload({
        generateUploadUrl: async () =>
          await generateR2UploadUrl({
            prefix: "spec-pilot/rows",
            filename: `${table._id}-rows.json.gz`,
          }),
        headers,
        rows,
      });

      await updateTableData({
        tableId: table._id,
        headers,
        rowsStorageKey: uploadedRows.rowsStorageKey,
        rowsFormat: uploadedRows.rowsFormat,
        rowCount: uploadedRows.rowCount,
        deletedRowIndices: metadata?.deletedRowIndices ?? [],
      });
      toast.success("Table saved successfully");
      setHasError(false);
    } catch (error) {
      toast.error("Failed to save table");
      console.error("Error saving table:", error);
      setHasError(true);
    }
  };

  const tableName = table.name || "Untitled Table";
  const rowCount = table.rows.length;
  const colCount = table.headers.length;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="h-[92vh] w-[98vw] max-w-[1760px]! gap-0 overflow-hidden p-0 flex flex-col" showCloseButton={false}>
        <DialogHeader className="px-6 py-4 border-b shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <DialogTitle className="flex items-center gap-2 text-lg truncate">
                <FileSpreadsheet className="size-5 shrink-0" />
                <span className="truncate">{tableName}</span>
              </DialogTitle>
              <DialogDescription className="mt-1.5 text-sm">
                Page {table.pageNumber} • {rowCount} rows × {colCount} columns
              </DialogDescription>
            </div>
            <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => gridRef.current?.addRow()}
                disabled={isSaving}
              >
                <Plus className="size-4" />
                Add Row
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => gridRef.current?.undoRowAction()}
                disabled={isSaving || !canUndoRowAction}
              >
                <Undo2 className="size-4" />
                Undo Row Action
              </Button>
              {hasChanges ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => gridRef.current?.cancel()}
                  disabled={isSaving}
                >
                  <X className="size-4" />
                  Cancel
                </Button>
              ) : null}
              {hasChanges ? (
                <Button
                  type="button"
                  size="sm"
                  onClick={() => void gridRef.current?.save()}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Save className="size-4" />
                  )}
                  {isSaving ? "Saving..." : "Save Changes"}
                </Button>
              ) : null}
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="shrink-0 -mr-2"
              onClick={onClose}
            >
              <X className="size-4" />
              <span className="sr-only">Close</span>
            </Button>
          </div>

          {hasError && (
            <Alert variant="destructive" className="mt-3">
              <AlertCircle className="size-4" />
              <AlertDescription>
                Failed to save changes. Please try again.
              </AlertDescription>
            </Alert>
          )}
        </DialogHeader>

        {/* Scrollable content area */}
        <div className="grid flex-1 gap-4 overflow-auto p-4 lg:grid-cols-[minmax(0,1fr)_minmax(380px,40%)] lg:p-6">
          <div className="min-w-0">
            <EditableDataGrid
              ref={gridRef}
              headers={table.headers}
              rows={table.rows}
              onSave={handleSave}
              hideActionBar
              onHasChangesChange={setHasChanges}
              onIsSavingChange={setIsSaving}
              onCanUndoRowActionChange={setCanUndoRowAction}
            />
          </div>
          {sourceId ? (
            <SpecPilotSourcePdfPanel
              sourcePdf={sourcePdf}
              sourceId={sourceId}
              pageNumber={table.pageNumber}
              className="h-[55vh] min-h-[360px] lg:h-full lg:min-h-0"
            />
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/*
 * Usage Example:
 *
 * import { TableEditorModal } from "@/components/table-editor-modal";
 *
 * function ExtractedTableCard({ table }) {
 *   const [isModalOpen, setIsModalOpen] = useState(false);
 *
 *   return (
 *     <>
 *       <Card>
 *         <Button onClick={() => setIsModalOpen(true)}>
 *           View Full Table
 *         </Button>
 *       </Card>
 *
 *       <TableEditorModal
 *         table={table}
 *         isOpen={isModalOpen}
 *         onClose={() => setIsModalOpen(false)}
 *       />
 *     </>
 *   );
 * }
 *
 * Accessibility Checklist:
 * - Dialog has proper ARIA attributes via shadcn/ui Dialog component
 * - Close button is keyboard accessible with visible focus state
 * - Error alerts are announced to screen readers
 * - Content area is scrollable for large tables
 *
 * Performance Considerations:
 * - Uses EditableDataGrid which manages local state to minimize re-renders
 * - Modal content only renders when opened
 * - Save operations are batched (not per-keystroke)
 */
